import { z } from 'zod';

/** Roles per ADR-004 E. There is deliberately no `client` role: Buakei AI has no client portal. */
export const RoleSchema = z.enum([
  'lawyer',
  'firm_admin',
  'student',
  'legal_editor',
  'legal_reviewer',
  'support_agent',
  'platform_admin',
]);
export type Role = z.infer<typeof RoleSchema>;

/** Top-level experiences shown in the workspace switcher. */
export const ExperienceSchema = z.enum(['professional', 'learning', 'editorial', 'operations']);
export type Experience = z.infer<typeof ExperienceSchema>;

/** Data workspaces (DATA-MODELS.md). Editorial and Operations are consoles, not data workspaces. */
export const WorkspaceKindSchema = z.enum(['personal', 'firm', 'learning']);
export type WorkspaceKind = z.infer<typeof WorkspaceKindSchema>;

export const PermissionSchema = z.enum([
  'research.use',
  'drafting.use',
  'documents.read',
  'learning.use',
  'editorial.view',
  'editorial.review',
  'editorial.approve',
  'operations.view',
  'firm.members.manage',
  'matter.read',
]);
export type Permission = z.infer<typeof PermissionSchema>;

export const WorkspaceSchema = z.object({
  id: z.string(),
  kind: WorkspaceKindSchema,
  name: z.string(),
  organizationId: z.string().optional(),
});
export type Workspace = z.infer<typeof WorkspaceSchema>;

export const MembershipSchema = z.object({
  workspaceId: z.string(),
  organizationId: z.string().optional(),
  roles: z.array(RoleSchema),
  /** Explicit matter grants. Administration roles never imply matter access (SEC-002). */
  matterGrants: z.array(z.string()).default([]),
});
export type Membership = z.infer<typeof MembershipSchema>;

/**
 * The scope every protected action consumes (SYSTEM-DESIGN §4).
 * On the server it MUST be derived from verified identity and trusted membership records,
 * never from client-supplied values.
 */
export const ScopedContextSchema = z.object({
  actorUid: z.string(),
  workspaceId: z.string(),
  workspaceKind: WorkspaceKindSchema,
  organizationId: z.string().optional(),
  matterId: z.string().optional(),
  permissions: z.array(PermissionSchema),
  matterGrants: z.array(z.string()),
});
export type ScopedContext = z.infer<typeof ScopedContextSchema>;

/**
 * Firebase `firebase.sign_in_provider` values Buakei AI accepts (ADR-006). Email-link sign-in
 * is reported as `password` (verified against the Auth emulator).
 */
export const SignInProviderSchema = z.enum(['password', 'google.com']);
export type SignInProvider = z.infer<typeof SignInProviderSchema>;

/**
 * `GET /api/v1/me` (API-CONTRACTS.md). Identity comes from the verified token; memberships and
 * workspaces come from trusted server records (M2-02, ADR-008).
 */
export const MeSchema = z.object({
  uid: z.string(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable(),
  signInProvider: SignInProviderSchema,
  memberships: z.array(MembershipSchema),
  workspaces: z.array(WorkspaceSchema),
  /** Set when memberships exist but are withheld, e.g. until the email is verified (ADR-008). */
  withheld: z.enum(['email_unverified']).optional(),
});
export type Me = z.infer<typeof MeSchema>;
